import { useState, useRef, useEffect, type SyntheticEvent as ReactSyntheticEvent } from 'react'
import { PlayIcon, PauseIcon, DownloadIcon } from '../icons'

export function WhatsAppAudioPlayer({ src, fileName, isOutgoing = false }: { src: string; fileName?: string; isOutgoing?: boolean }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !src) return

    setLoading(true)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0)
      setLoading(false)
    }
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }
    const handleCanPlay = () => setLoading(false)
    const handleError = () => setLoading(false)

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('error', handleError)

    // Force load in case the browser didn't auto-start
    audio.load()

    // Fallback: if metadata doesn't load in 5s, hide spinner
    const fallbackTimer = setTimeout(() => {
      setLoading(false)
    }, 5000)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('error', handleError)
      clearTimeout(fallbackTimer)
    }
  }, [src])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play().catch(() => {
        // If play fails, try loading first then playing
        audio.load()
        audio.play().catch(() => {})
      })
      setIsPlaying(true)
    }
  }

  const formatTime = (secs: number) => {
    if (!secs || isNaN(secs)) return '0:00'
    const m = Math.floor(secs / 60)
    const s = Math.floor(secs % 60)
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const handleDownload = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    try {
      const res = await fetch(src)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileName || 'audio'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      // Fallback: open in new tab
      window.open(src, '_blank')
    }
  }

  return (
    <div className={`wa-audio-player ${isOutgoing ? 'outgoing' : 'incoming'}`}>
      <audio ref={audioRef} src={src} preload="metadata" crossOrigin="anonymous" />

      <button className="wa-audio-play-btn" onClick={togglePlay} disabled={loading}>
        {loading ? (
          <div className="wa-audio-spinner" />
        ) : isPlaying ? (
          <PauseIcon size={20} />
        ) : (
          <PlayIcon size={20} />
        )}
      </button>

      <div className="wa-audio-waveform">
        <div className="wa-audio-progress-bar">
          <div className="wa-audio-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="wa-audio-time">
          {formatTime(isPlaying ? currentTime : duration)}
        </div>
      </div>

      <button
        className="wa-audio-download"
        onClick={handleDownload}
        title="Baixar áudio"
      >
        <DownloadIcon size={16} />
      </button>
    </div>
  )
}
